import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { AbcCore, CollectionHelper, CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import { UserService } from '@barthauer/abc/core/authentication';
import { ICoreDataEntity, PersistMode } from '@barthauer/abc/core/data';
import { ErrorHandlingService, ErrorInfo } from '@barthauer/abc/core/error-handling';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  AbcPopupComponent,
  AbcRadioGroupComponent,
  _AbcButtonComponent,
  _AbcGridColumn,
  _AbcGridComponent,
  _AbcSelectBoxComponent,
} from '@barthauer/abc/ui-components';
import { DataSource, ArrayStore } from 'devextreme/common/data';
import { FocusedRowChangedEvent, RowRemovingEvent, RowUpdatingEvent } from 'devextreme/ui/data_grid';
import { ItemClickEvent } from 'devextreme/ui/select_box';
import { ObjectSelectionItem, ObjectsSet, ObjectsSetHelper, ObjectsSetsVisibility } from './models/objects-set';
import { ObjectsSetsColumnInfo, ObjectsSetsSettings, OrderUnit } from './objects-sets-column-info';
import { ObjectsSetsService } from './objects-sets.service';
import {
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';

@Component({
    selector: 'abc-ui-frameworks-objects-sets',
    templateUrl: './objects-sets.component.html',
    styleUrls: ['./objects-sets.component.scss'],
    providers: [{ provide: ObjectsSetsService, deps: [AbcCore, Injector] }],
    standalone: false
})
export class ObjectsSetsComponent implements OnInit {
  private resAsync: Promise<ResourceSet>;
  public isRowDeleted = false;
  public isReadOnly = false;
  public canSave = false;
  public res: ResourceSet = ResourceSet.Empty;
  @ViewChild('abcPopup', { static: true }) abcPopup: AbcPopupComponent;
  @ViewChild('addOrOverwritePopup', { static: true })
  addOrOverwritePopup: AbcPopupComponent;
  @ViewChild('filterBySelectedObjectSetsButton', { static: true })
  filterBySelectedObjectSetsButton: _AbcButtonComponent;
  @ViewChild('addOrOverwriteRadioGroup', { static: true })
  addOrOverwriteRadioGroup: AbcRadioGroupComponent;
  @ViewChild('orderUnitPopup', { static: true })
  orderUnitPopup: AbcPopupComponent;
  @ViewChild('objectSetsGrid', { static: true })
  objectSetsGrid: _AbcGridComponent;
  @ViewChild('orderUnitSelectBox', { static: true })
  orderUnitSelectBox: _AbcSelectBoxComponent;
  @ViewChild('orderUnitTypeSelectBox', { static: true })
  orderUnitTypeSelectBox: _AbcSelectBoxComponent;
  public orderUnitDataSource: DataSource<OrderUnit[]>;
  public selectedOrderUnitData: { Designation: string; Id: string }[] = [];
  public referencedObject: ReferencedObject = {
    objectType: Number(),
    id: '',
    designation: '',
  };
  public onSelectEntities: (entities: {
    idExpression: string;
    objectTypeExpression: string;
    entities: ObjectSelectionItem[];
  }) => void;

  public sourceDataEntities: <T>() => DataEntitiesWithMetaData<T>;
  private selectedObjectsSet: ObjectsSet;
  private transformedSourceDataEntities: ICoreDataEntity[] = [];
  private persistMode: PersistMode | undefined;
  public set parentObjectType(objectType: string) {
    this.objectsSetsColumnInfo = new ObjectsSetsColumnInfo(objectType, this.injector);
  }

  public objectsSetsColumnInfo: ObjectsSetsColumnInfo;
  public entities: { [key: string]: DataSource<ObjectSelectionItem, string> } = {};
  public objectsSetsStore: DataSource<ObjectsSet>;

  public columns: _AbcGridColumn[];
  public addOrOverwriteChoices: Array<{
    value: AddOrOverwriteChoices;
    caption: string;
  }> = [];
  constructor(
    private objectsSetsService: ObjectsSetsService,
    private injector: Injector,
    private resourceManagerService: ResourceManagerService,
    private messageBoxService: MessageBoxService
  ) {
    this.resAsync = this.resourceManagerService.getResourceSet('abc-ui-frameworks-objects-sets');
  }
  public async ngOnInit(): Promise<void> {
    await this.setColumns();
    await this.setAddOrOverwriteChoices();
    this.isReadOnly = await this.objectsSetsService.isReadOnly();
    if (this.sourceDataEntities()?.entities?.length) {
      this.res = await this.resAsync;
      const filteredObjectsSelections: ObjectsSet[] = (await this.objectsSetsService.getObjectsSelections()).filter(
        (_) => _.isVisibleForCurrentUser === true
      );
      this.objectsSetsStore = new DataSource(
        new ArrayStore({
          key: 'id',
          data: filteredObjectsSelections,
        })
      );
      if (ObjectsSetsSettings.orderUnits) {
        this.orderUnitDataSource = new DataSource({
          store: ObjectsSetsSettings.orderUnits,
          key: 'value',
        });
      }

      if (!CompareHelper.isEmptyObject(filteredObjectsSelections)) {
        await this.setFocusedRow(filteredObjectsSelections[0].id);
        this.selectedObjectsSet = filteredObjectsSelections[0];
        this.canSave = !this.isReadOnly;
        this.filterBySelectedObjectSetsButton.disabled = false;
      }
      this.objectSetsGrid.totalCountVisibility = true;
      this.objectSetsGrid.instance.refresh();
      this.transformSourceDataEntities();
    }
    this.objectsSetsService.core.persist.afterPersistPendingChanges$.subscribe((obj) => {
      if (obj instanceof ErrorInfo) {
        this.showError(obj.message);
      } else if (obj[0].persistenceCategory === this.objectsSetsService.persistenceCategory) {
        const id = obj[0].id;
        if (id) {
          if (this.persistMode === PersistMode.Update) {
            this.updateGridObjectById(id);
          }
        }
        this.persistMode = undefined;
      }
    });
  }

  private async setColumns() {
    let res: ResourceSet = ResourceSet.Empty;
    res = await this.resAsync;
    this.columns = [
      {
        dataField: 'id',
        visible: false,
      },
      {
        dataField: 'name',
        caption: res.get('Designation'),
        dataType: 'string',
      },
      {
        dataField: 'entitiesCount',
        caption: '#',
        allowEditing: false,
        width: '60px',
        dataType: 'number',
      },
      {
        dataField: 'visibility',
        caption: res.get('Visibility'),
        width: '80px',
        dataType: 'object',
        lookup: {
          dataSource: [
            {
              caption: res.get('Visibility-Private'),
              name: ObjectsSetsVisibility.Private,
            },
            {
              caption: res.get('Visibility-Public'),
              name: ObjectsSetsVisibility.Public,
            },
          ],
          displayExpr: 'caption',
          valueExpr: 'name',
        },
      },
      {
        dataField: 'orderUnitType',
        caption: res.get('OrderUnittype'),
        allowEditing: false,
        visible: false,
        dataType: 'string',
        width: '50x',
      },
      {
        dataField: 'lastSavedDateTime',
        caption: res.get('LastSaved'),
        allowEditing: false,
        dataType: 'datetime',
        width: '150px',
        format: {
          formatter: (dateTime) => ConvertHelper.toLocaleFormattedDateTimeString(dateTime),
        },
      },
    ];
  }

  private async setAddOrOverwriteChoices() {
    let res: ResourceSet = ResourceSet.Empty;
    res = await this.resAsync;
    this.addOrOverwriteChoices = [
      {
        caption: res.get('AddOrOverwriteObject-SaveOnlyNewObjects'),
        value: AddOrOverwriteChoices.SaveOnlyNewObjects,
      },
      {
        caption: res.get('AddOrOverwriteObject-AddObjectsToTheExistingOnes'),
        value: AddOrOverwriteChoices.AddObjectsToTheExistingOnes,
      },
    ];
  }

  private transformSourceDataEntities(): void {
    const sourceDataEntities = this.sourceDataEntities();
    const masterIdExpr = sourceDataEntities?.idExpression(this.objectsSetsColumnInfo.idExpression);
    const masterObjectTypeRef: string = sourceDataEntities.objectTypeExpression(
      this.objectsSetsColumnInfo.objectTypeExpression
    );
    const parentEntities = this.sourceDataEntities().entities;

    for (let index = 0; index < parentEntities.length; index++) {
      const entity: any = parentEntities[index];
      this.transformedSourceDataEntities.push({
        Id: entity[masterIdExpr],
        ObjectType: entity[masterObjectTypeRef],
      });
    }
  }

  private async ensureRowVisible(id: string): Promise<void> {
    this.objectSetsGrid.ensureRowVisible(id);
  }

  private async setFocusedRow(id: string): Promise<void> {
    this.objectSetsGrid.focusedRowKey = id;
    await this.ensureRowVisible(id);
  }

  public onFocusedRowChanged(e: FocusedRowChangedEvent<ObjectsSet, ObjectsSet>): void {
    this.selectedObjectsSet = e.row?.data as ObjectsSet;
  }

  public async filterBySelectedObjectSet(): Promise<void> {
    let entities: ObjectSelectionItem[] = [];

    if (this.objectsSetsService.selectedEntities.entities?.length > 0) {
      entities = this.objectsSetsService.selectedEntities.entities;
    } else if (!CompareHelper.isEmptyObject(this.selectedObjectsSet)) {
      entities = await this.selectedObjectsSet.entities();
    }

    if (entities.length > 0) {
      entities = CollectionHelper.distinct(entities, (item) => item.Id);
      const idExpression: string = this.objectsSetsColumnInfo.idExpression;
      const objectTypeExpression: string = this.objectsSetsColumnInfo.objectTypeExpression;
      await this.sendSelectEntitiesAndClose(idExpression, objectTypeExpression, entities);
    }
  }

  public loadObjectsSetsWindow(): void {
    if (this.sourceDataEntities()?.entities?.length) {
      this.abcPopup.visible = true;
    }
  }

  public async onRowUpdating(e: RowUpdatingEvent<ObjectsSet, string>) {
    if (e.newData?.visibility) {
      e.oldData?.changeVisibility(e.newData.visibility);
    }
    this.persistMode = PersistMode.Update;
    e.oldData?.persistPendingObject();
  }

  public onRowRemoving(e: RowRemovingEvent<ObjectsSet>): void {
    const htmlMessage = this.res.get('Info-Message');
    const settings = new MessageSettings();
    settings.mode = MessageMode.Info;
    settings.icon = MessageBoxIcon.Question;
    settings.button = MessageBoxButtons.YesNo;
    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMessage;

    e.cancel = true;

    this.messageBoxService.showMessageBox(settings).subscribe(async (resultObject: any) => {
      const result = resultObject.result;
      if (result === 6) {
        this.isRowDeleted = true;
        try {
          await this.objectsSetsService.deleteObjectsSet(e.data.id);
          await this.objectSetsGrid?.instance.getDataSource().store().remove(e.data.id);
          await this.objectSetsGrid?.instance.getDataSource().reload();
        } finally {
          this.isRowDeleted = false;
        }
      }
    });
  }

  private async updateGridObjectById(id: string): Promise<void> {
    const gridObject = <ObjectsSet>await this.objectsSetsService.getObjectsSelectionById(id);
    await this.objectSetsGrid.instance.getDataSource().store().update(gridObject?.id, gridObject);
  }

  private async insertGridObject(selection: ObjectsSet): Promise<void> {
    await this.objectSetsGrid.instance.getDataSource().store().insert(selection);
    await this.objectSetsGrid.instance.getDataSource().reload();
    await this.setFocusedRow(selection.id);
  }

  public async sendSelectEntitiesAndClose(
    idExpression: string,
    objectTypeExpression: string,
    entities: ObjectSelectionItem[]
  ) {
    this.onSelectEntities({
      idExpression: idExpression,
      objectTypeExpression: objectTypeExpression,
      entities: entities,
    });
    await this.abcPopup.instance.hide();
  }

  public async saveOrOverwriteObjectSet(): Promise<void> {
    const value: number = this.addOrOverwriteRadioGroup?.value;
    const selectedEntities = await this.selectedObjectsSet.entities();
    if (value === AddOrOverwriteChoices.AddObjectsToTheExistingOnes) {
      const newEntities: ICoreDataEntity[] = [];
      for (let index = 0; index < this.transformedSourceDataEntities.length; index++) {
        const entity = this.transformedSourceDataEntities[index];
        const foundEntity = selectedEntities.find((e) => e.Id === entity.Id);
        if (!foundEntity) {
          newEntities.push(entity);
        }
      }
      if (newEntities) {
        try {
          await this.objectsSetsService.updateObjectsSet(this.selectedObjectsSet.id, newEntities);
          this.selectedObjectsSet.entitiesCount += newEntities.length;
          this.objectSetsGrid.instance.getDataSource().reload();
          await this.addOrOverwritePopup.instance.hide();
        } catch (err) {
          this.showError(err);
        }
      }
    } else if (value === AddOrOverwriteChoices.SaveOnlyNewObjects) {
      try {
        const p = await this.objectsSetsService.updateObjectsSet(
          this.selectedObjectsSet.id,
          this.transformedSourceDataEntities,
          true,
          selectedEntities
        );
        this.selectedObjectsSet.entitiesCount = this.transformedSourceDataEntities.length;
        this.objectSetsGrid.instance.getDataSource().reload();
        await this.addOrOverwritePopup.instance.hide();
      } catch (err) {
        this.showError(err);
      }
    }
  }

  public openSaveOrOverwritePopup(): void {
    this.addOrOverwritePopup.visible = true;
  }

  public async saveSourceDataEntites(referencedObject?: ReferencedObject): Promise<void> {
    if (this.sourceDataEntities) {
      const designation: string = await this.objectSetDesignation();
      const sourceDataEntities = this.sourceDataEntities();
      const masterIdExpr = sourceDataEntities?.idExpression(this.objectsSetsColumnInfo.idExpression);
      const masterObjectTypeRef: string = sourceDataEntities.objectTypeExpression(
        this.objectsSetsColumnInfo.objectTypeExpression
      );
      const parentEntities = this.sourceDataEntities().entities;
      const entities: ICoreDataEntity[] = [];
      for (let index = 0; index < parentEntities.length; index++) {
        const entity: any = parentEntities[index];
        entities.push({
          Id: entity[masterIdExpr],
          ObjectType: entity[masterObjectTypeRef],
        });
      }
      const selection: ObjectsSet = (await this.objectsSetsService.saveObjectsSet(
        referencedObject?.designation ? referencedObject.designation : designation,
        entities,
        referencedObject?.id,
        referencedObject?.objectType
      )) as ObjectsSet;

      if (selection && selection instanceof ObjectsSet) {
        this.insertGridObject(selection);
      }
    }
  }

  public openOrderUnitPopup(): void {
    this.orderUnitTypeSelectBox?.instance?.reset();
    this.selectedOrderUnitData = [];
    this.orderUnitPopup.visible = true;
  }

  public async saveSourceDataEntitesWithOrderUnit(): Promise<void> {
    try {
      const referencedObject = this.referencedObject?.designation !== 'Other' ? this.referencedObject : undefined;
      await this.saveSourceDataEntites(referencedObject);
      await this.closeOrderUnitPopup();
    } catch (err) {
      this.showError(err);
    }
  }

  private showError(err: any) {
    const error = this.injector.get(ErrorHandlingService);
    if (error) {
      error.publish(err);
    } else {
      console.error(err);
    }
  }

  public async closeOrderUnitPopup(): Promise<void> {
    await this.orderUnitPopup.instance.hide();
  }

  public async closeAddOrOverwritePopup(): Promise<void> {
    await this.addOrOverwritePopup.instance.hide();
  }

  public async onOrderUnitTypeValueChange(event: ItemClickEvent): Promise<void> {
    const itemData: OrderUnit = event.itemData;
    if (itemData.name !== 'Other') {
      this.selectedOrderUnitData = await itemData.data();
    } else {
      this.selectedOrderUnitData = [{ Designation: await this.objectSetDesignation(true), Id: 'Other' }];
      this.orderUnitSelectBox.value = 'Other';
    }
  }

  public onOrderUnitValueChange(event: ItemClickEvent): void {
    this.referencedObject.designation = event.itemData?.Designation;
  }

  public async toggleOrderUnitTypeColumn(): Promise<void> {
    const column = this.objectSetsGrid.columns.find((c) => c.dataField === 'orderUnitType');
    if (column) {
      column.visible = column.visible ? false : true;
    }
    this.objectSetsGrid.instance.columnOption('orderUnitType', 'visible', column?.visible);
    await this.objectSetsGrid.instance.refresh();
  }

  private async objectSetDesignation(withoutUserName?: boolean): Promise<string> {
    const applicationTitle = document.title;
    const lastSavedDateTime = ObjectsSetHelper.getFormattedDateTime(new Date());
    let designation: string;
    if (!withoutUserName) {
      const currentUserInfo = await this.injector.get(UserService).getCurrentLoginInfo().toPromise();
      designation = applicationTitle + ' ' + lastSavedDateTime + '$' + currentUserInfo.displayName;
    } else {
      designation = applicationTitle + ' ' + lastSavedDateTime;
    }
    return designation;
  }
}

export interface DataEntitiesWithMetaData<T> {
  objectType: string;
  entities: T[];
  idExpression: (id: string) => string;
  objectTypeExpression: (objectType: string) => string;
}

interface ReferencedObject {
  objectType: number;
  id: string;
  designation: string;
}

enum AddOrOverwriteChoices {
  SaveOnlyNewObjects,
  AddObjectsToTheExistingOnes,
}
